"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { coverImageObjectPosition, coverImageSrc } from "@/lib/cover-image-meta";
import { EDITORIAL_LABELS } from "@/lib/editorial-labels";

export type CheckThisArticle = {
  id: string;
  title: string;
  slug: string;
  coverImage: string;
  coverImageMeta?: unknown;
  categoryName: string;
  authorName: string;
};

type ArticleViewCountsResponse = {
  counts?: Record<string, number>;
};

type AdminViewCountsState = {
  articleIdsKey: string;
  counts: Record<string, number>;
};

const PAGE_SIZE = 9;

function EmptySectionState({ message }: { message: string }) {
  return <p className="text-sm font-semibold text-[#6B6B6B]">{message}</p>;
}

export function CheckThisSection({
  articles,
  emptyStateMessage,
  title = EDITORIAL_LABELS.homepage.latestNews,
}: {
  articles: CheckThisArticle[];
  emptyStateMessage?: string;
  title?: string;
}) {
  const [page, setPage] = useState(0);
  const [adminViewCounts, setAdminViewCounts] = useState<AdminViewCountsState | null>(null);
  const articleIds = useMemo(() => articles.map((article) => article.id), [articles]);
  const articleIdsKey = articleIds.join(",");
  const totalPages = Math.ceil(articles.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(0, totalPages - 1));
  const visibleArticles = articles.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const showPagination = totalPages > 1;

  useEffect(() => {
    if (!articleIds.length) return;

    const controller = new AbortController();
    const params = new URLSearchParams({ ids: articleIdsKey });

    fetch(`/api/admin/articles/view-counts?${params.toString()}`, {
      credentials: "same-origin",
      cache: "no-store",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) return null;
        return (await response.json()) as ArticleViewCountsResponse;
      })
      .then((data) => {
        if (!controller.signal.aborted) {
          setAdminViewCounts(data?.counts ? { articleIdsKey, counts: data.counts } : null);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setAdminViewCounts(null);
        }
      });

    return () => controller.abort();
  }, [articleIds, articleIdsKey]);

  const visibleAdminViewCounts =
    adminViewCounts?.articleIdsKey === articleIdsKey ? adminViewCounts.counts : null;

  if (!articles.length && !emptyStateMessage) return null;

  return (
    <section className="mt-12 border-b border-[#d9d7cf] pb-12">
      <div className="mb-6 border-t-2 border-[#A4243B] pt-3">
        <h2 className="font-display text-2xl uppercase leading-none text-[#111111] md:text-3xl">
          {title}
        </h2>
      </div>

      {visibleArticles.length ? (
        <div className="grid gap-x-7 gap-y-9 md:grid-cols-3">
          {visibleArticles.map((article) => (
            <Link key={article.id} href={`/article/${article.slug}`} className="group block">
              <div className="relative aspect-[16/9] overflow-hidden rounded-md bg-zinc-100">
                {article.coverImage && (
                  <Image
                    src={coverImageSrc(article.coverImage, article.coverImageMeta, "800")}
                    alt={article.title}
                    fill
                    sizes="(max-width: 768px) 100vw, 33vw"
                    className="object-cover transition duration-500 group-hover:scale-[1.02]"
                    style={{ objectPosition: coverImageObjectPosition(article.coverImageMeta) }}
                  />
                )}
              </div>
              <div className="mt-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-[#A4243B]">
                  {article.categoryName}
                </p>
                <h3 className="mt-1.5 font-sans text-2xl font-bold leading-[1.02] text-[#111111] group-hover:underline md:text-[1.7rem]">
                  {article.title}
                </h3>
                <p className="mt-3 text-[10px] font-semibold uppercase tracking-widest text-[#6B6B6B]">
                  {article.authorName}
                  {visibleAdminViewCounts ? ` / ${visibleAdminViewCounts[article.id] ?? 0} MEGTEKINTÉS` : ""}
                </p>
              </div>
            </Link>
          ))}
        </div>
      ) : emptyStateMessage ? (
        <EmptySectionState message={emptyStateMessage} />
      ) : null}

      {showPagination ? (
        <div className="mt-7 flex justify-end">
          <div className="flex items-center gap-2">
            <span className="text-xs font-black tabular-nums text-[#6B6B6B]">
              {safePage + 1} / {totalPages}
            </span>
            <button
              type="button"
              aria-label="Előző cikkek"
              disabled={safePage === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              className="grid h-8 w-8 place-items-center border border-[#d8d2c5] bg-white text-[#111111] shadow-sm transition hover:border-[#A4243B] disabled:pointer-events-none disabled:opacity-35"
            >
              <ChevronLeft size={16} aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label="Következő cikkek"
              disabled={safePage >= totalPages - 1}
              onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
              className="grid h-8 w-8 place-items-center border border-[#d8d2c5] bg-white text-[#111111] shadow-sm transition hover:border-[#A4243B] disabled:pointer-events-none disabled:opacity-35"
            >
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
